import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  Phone,
  ArrowUpRight,
  HeartPulse,
  Stethoscope,
  Siren,
  Pill,
  ScanLine,
  FlaskConical,
  UserRound,
  BedDouble,
  HandHeart,
  ShieldCheck,
  MapPin,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { HOSPITAL, FACILITIES, DEPARTMENTS, TPA_PARTNERS, VALUES } from "../data/site";

const ICONS = {
  HeartPulse,
  Stethoscope,
  Siren,
  Pill,
  ScanLine,
  FlaskConical,
  UserRound,
  BedDouble,
  HandHeart,
};

const Stat = ({ k, v }) => (
  <div>
    <div className="font-serif-display text-3xl md:text-4xl text-brand-ink">{v}</div>
    <div className="text-xs uppercase tracking-[0.18em] text-brand-ink/55 mt-1">{k}</div>
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fhc-fade-up">
            <div className="inline-flex items-center gap-2 bg-brand-maroon/10 text-brand-maroon px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon"></span>
              Now serving Garhi Harsaru, Gurugram
            </div>
            <h1 className="mt-5 font-serif-display text-4xl md:text-6xl lg:text-7xl text-brand-ink leading-[1.02]">
              Compassionate care, <br />
              <span className="italic text-brand-maroon">close to home.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-brand-ink/70 max-w-xl leading-relaxed">
              A trusted multi-speciality hospital with 24x7 emergency, modern OT, ICU,
              expert OPD consultants and the best nursing staff — all under one roof,
              at honest, family-friendly prices.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-maroon text-white text-sm font-semibold px-6 py-3.5 hover:bg-brand-maroonDark transition"
              >
                Book an appointment
                <ArrowUpRight size={16} />
              </Link>
              <a
                href={`tel:${HOSPITAL.phoneDial}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 rounded-full border border-brand-ink/15 text-brand-ink text-sm font-semibold px-6 py-3.5 hover:bg-white transition"
              >
                <Phone size={15} />
                {HOSPITAL.phone}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat k="Years caring" v="10+" />
              <Stat k="Specialists" v="15+" />
              <Stat k="ER cover" v="24x7" />
            </div>
          </div>

          <div className="md:col-span-5 fhc-fade-up delay-200">
            <div className="relative">
              <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1758691462482-2b6ccbaefa6e?w=1200&q=85"
                  alt="Doctor with patient and family"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent"></div>
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-maroon/10 flex items-center justify-center">
                      <ShieldCheck size={18} className="text-brand-maroon" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-brand-ink/55">Cashless</div>
                      <div className="text-sm font-semibold text-brand-ink">All major TPAs accepted</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-5 -left-5 hidden md:block bg-brand-cream border border-brand-beige rounded-2xl p-4 shadow-sm">
                <div className="text-xs uppercase tracking-[0.18em] text-brand-maroon font-bold">Open now</div>
                <div className="text-sm font-semibold text-brand-ink mt-1">Emergency • 24×7</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TPA marquee */}
      <section data-testid="tpa-marquee" className="border-y border-brand-beige bg-white py-7">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-ink/50 shrink-0">
            Empaneled with
          </div>
          <div className="flex-1 w-full overflow-hidden">
            <Marquee gradient gradientColor="white" gradientWidth={64} speed={32} pauseOnHover>
              {[...TPA_PARTNERS, ...TPA_PARTNERS, ...TPA_PARTNERS].map((p, i) => (
                <span
                  key={i}
                  className="mx-12 font-serif-display text-2xl text-brand-ink/40 whitespace-nowrap"
                >
                  {p.name}
                </span>
              ))}
            </Marquee>
          </div>
        </div>
      </section>

      {/* FACILITIES bento */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon mb-3">Facilities</div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-brand-ink leading-[1.05]">
              Built for serious care, <br />
              <span className="italic">designed for comfort.</span>
            </h2>
          </div>
          <p className="text-brand-ink/65 max-w-md md:text-base">
            From a fully-equipped ICU to a modern pharmacy, every department is engineered to deliver safe, timely
            and dignified care.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {FACILITIES.map((f, idx) => {
            const Icon = ICONS[f.icon] || Stethoscope;
            const span =
              idx === 0
                ? "md:col-span-6 md:row-span-2"
                : idx === 4
                ? "md:col-span-6"
                : "md:col-span-3";
            const tall = idx === 0;
            return (
              <article
                key={f.key}
                data-testid={`facility-card-${f.key}`}
                className={`group relative ${span} rounded-3xl overflow-hidden bg-white border border-brand-beige hover:shadow-xl transition-shadow`}
              >
                <div className={`relative ${tall ? "aspect-[4/5]" : "aspect-[5/4]"} overflow-hidden`}>
                  <img
                    src={f.image}
                    alt={f.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full px-3 py-1.5">
                    <Icon size={14} className="text-brand-maroon" />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-ink">{f.title}</span>
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <h3 className="font-serif-display text-2xl md:text-3xl">{f.title}</h3>
                    <p className="mt-2 text-sm text-white/85 line-clamp-3 max-w-md">{f.summary}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to="/facilities"
            data-testid="all-facilities-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-maroon hover:gap-3 transition-all"
          >
            See all facilities <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* DEPARTMENTS */}
      <section className="bg-brand-beige/60 py-20 md:py-28 border-y border-brand-beige">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon mb-3">Departments</div>
              <h2 className="font-serif-display text-3xl md:text-5xl text-brand-ink leading-[1.05]">
                Specialists for every <br /> stage of life.
              </h2>
            </div>
            <p className="md:col-span-5 text-brand-ink/65">
              Daily OPDs across General Medicine, Gynecology, Pediatrics, Surgery, Orthopedics and Dermatology — with
              short waiting times and friendly faces.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {DEPARTMENTS.map((d) => (
              <Link
                to="/departments"
                key={d.key}
                data-testid={`dept-card-${d.key}`}
                className="group relative rounded-2xl overflow-hidden bg-white border border-brand-beige p-7 hover:shadow-lg transition"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-maroon/8 flex items-center justify-center text-brand-maroon">
                  <Stethoscope size={20} />
                </div>
                <h3 className="font-serif-display text-2xl text-brand-ink mt-5">{d.name}</h3>
                <p className="text-sm text-brand-ink/65 mt-2 leading-relaxed">{d.description}</p>
                <span className="absolute top-7 right-7 text-brand-ink/30 group-hover:text-brand-maroon transition">
                  <ArrowUpRight size={20} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <div className="relative rounded-3xl overflow-hidden aspect-square">
            <img
              src="https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?w=1400&q=85"
              alt="Caring nurse"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-maroon mb-3">Why families choose us</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-brand-ink leading-[1.05]">
            Care that listens. <br />
            <span className="italic">Bills you can read.</span>
          </h2>
          <ul className="mt-8 space-y-5">
            {VALUES.map((v) => (
              <li key={v.title} className="flex gap-4">
                <CheckCircle2 className="shrink-0 mt-0.5 text-brand-maroon" size={22} />
                <div>
                  <div className="font-semibold text-brand-ink">{v.title}</div>
                  <div className="text-sm text-brand-ink/65 leading-relaxed">{v.text}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div className="relative rounded-3xl overflow-hidden bg-brand-maroon text-white p-8 md:p-14">
          <div className="grid md:grid-cols-12 gap-10 items-center relative z-10">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#F8C7CB] mb-3">
                We're here for you
              </div>
              <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05]">
                Need to speak with a doctor today?
              </h3>
              <p className="mt-4 text-white/80 max-w-md">
                Call our front desk and we'll arrange the right OPD slot, scan, or admission for you and your family.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={`tel:${HOSPITAL.phoneDial}`}
                  data-testid="cta-call-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-brand-maroon font-semibold px-6 py-3 hover:bg-[#F8C7CB] transition"
                >
                  <Phone size={16} /> {HOSPITAL.phone}
                </a>
                <Link
                  to="/contact"
                  data-testid="cta-message-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold px-6 py-3 hover:bg-white/10 transition"
                >
                  Send a message <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-[#F8C7CB]" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#F8C7CB] font-bold">Visit us</div>
                  <div className="text-sm mt-1 leading-relaxed">{HOSPITAL.address}</div>
                </div>
              </div>
              <div className="mt-5 flex items-start gap-3">
                <Clock size={18} className="mt-0.5 text-[#F8C7CB]" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#F8C7CB] font-bold">Hours</div>
                  <div className="text-sm mt-1">{HOSPITAL.hoursLabel}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
